/**
 * ★ WD — corrigir os VALORES DE DINHEIRO do lead (`valorFechado` e `valorEntrada`).
 *
 * A tela não decide nada: ela pergunta aqui. Tudo neste arquivo é puro — nenhuma leitura de Firestore,
 * nenhum React —, porque decisão sobre dinheiro precisa ser testável sem montar nada.
 *
 * ★★ POR QUE ESTA PEÇA EXISTE. Medido em `docs/wd-medicao.md`: `valorFechado` e `valorEntrada` só eram
 * escritos pelo diálogo de MUDANÇA DE ETAPA, e `moverEtapa` sai cedo quando a etapa não muda
 * (`if (lead.etapa === novaEtapa) return;`). Um lead já em `fechado` não tinha NENHUM caminho de escrita —
 * não era permissão, era ausência de caminho.
 *
 * ★★ A ASSIMETRIA É DELIBERADA, NÃO ESQUECIMENTO — e a linha é O QUE MOVE RELATÓRIO.
 *
 * **Só o TOTAL DA VENDA (`valorFechado`) exige motivo** e vai para a timeline com de-para. Ele é o número
 * que entra em metas, painel e placar — mexer nele muda relatório, inclusive de meses passados.
 *
 * **Valor de oportunidade (`valorOrcado`), entrada (`valorEntrada`), código do paciente e nome no sistema
 * gravam DIRETO, sem motivo e sem timeline**: são campos OPERACIONAIS, preenchidos e corrigidos no meio do
 * atendimento, e atrito ali atrapalha o trabalho sem proteger número nenhum.
 *
 * ★ Decisão do dono (25/set/2026). **Quem "uniformizar" os cinco está desfazendo uma decisão, não limpando
 * uma inconsistência** — e há guarda de fonte que falha se esta explicação sair do código.
 *
 * ★ E a comissão NÃO depende destes campos: a folha lê `compareceuEm`/agendamentos e `recebimento.valor`,
 * documento a documento (medido: zero ocorrências dos campos em `functions/remuneracao` e
 * `web/src/remuneracao`). Corrigir aqui muda RELATÓRIO, nunca pagamento.
 */
import { paraNumero } from '@/leads/recebimentos';

/** Quem pode corrigir dinheiro: qualquer pessoa de VENDAS (decisão do dono). */
export const PAPEIS_QUE_CORRIGEM_VALORES = ['gestor', 'sdr', 'superadmin'];

const brl = (v) => paraNumero(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * O diálogo de correção pode abrir?
 *
 * ★ `mesFechado` entra como PARÂMETRO, e não como leitura aqui dentro, de propósito. Hoje ele chega SEMPRE
 * `false`: a saída (A) do plano adiou a trava, porque a SDR **não pode ler** `clinicas/{id}/fechamentos/{mes}`
 * (`allow read: if isGestor` — o cabeçalho carrega os totais da folha, e abri-lo vazaria a folha). Quando o
 * sinal existir num lugar que a SDR possa ler, **só o chamador muda** — esta função já está pronta.
 */
export function podeCorrigirValores(lead, usuario, { mesFechado = false } = {}) {
  if (lead?.etapa !== 'fechado') return false;
  if (mesFechado === true) return false;
  return PAPEIS_QUE_CORRIGEM_VALORES.includes(usuario?.papel);
}

/**
 * Pode salvar? Devolve `null` quando sim, ou a MENSAGEM EM PORTUGUÊS quando não.
 *
 * ★ O caso do zero é o que mais importa. As rules exigem `valorFechado is number && valorFechado > 0`
 * enquanto a etapa for `fechado` (`firestore.rules:61`), e em `update` a validação é sobre o documento
 * RESULTANTE. Zerar é recusado pelo servidor com `permission-denied`, cuja mensagem crua é "Missing or
 * insufficient permissions". Esta função existe para o usuário NUNCA chegar lá — e o texto diz o CAMINHO
 * CERTO, não só a proibição: o caso real de "zerar" quase nunca é "o valor é zero", é "este lead não devia
 * estar fechado", e o remédio para isso é a ETAPA.
 *
 * ★ A trava da entrada é CONVENIÊNCIA DE TELA, NÃO GARANTIA: as rules não dizem nada sobre `valorEntrada`
 * (medido), então o servidor ACEITA entrada maior que o total. Um script Admin SDK passaria. Está escrito
 * aqui porque uma trava que parece garantia é pior do que uma trava declarada.
 */
export function validarCorrecao({ etapa, valorFechado } = {}) {
  const total = paraNumero(valorFechado);
  if (etapa === 'fechado' && !(total > 0)) {
    return 'Um lead fechado precisa de um total da venda maior que zero. Se a venda não aconteceu, mude a etapa para "Não fechou" (com o motivo), em vez de zerar o valor.';
  }
  return null;
}

/**
 * A ENTRADA, validada no caminho DIRETO (sem motivo).
 *
 * ★ Isto é CONVENIÊNCIA DE TELA, NÃO GARANTIA: as rules não dizem nada sobre `valorEntrada` (medido), então
 * o servidor ACEITA entrada maior que o total. Um script Admin SDK passaria. Está escrito porque trava que
 * parece garantia é pior do que trava declarada.
 */
export function validarEntrada({ valorFechado, valorEntrada } = {}) {
  const total = paraNumero(valorFechado);
  const entrada = paraNumero(valorEntrada);
  if (entrada < 0) return 'A entrada não pode ser negativa.';
  // ★ WD-c2 — TEXTO NEUTRO ("está maior", não "não pode"): a MESMA função serve os dois momentos, e eles
  // fazem coisas diferentes com ela. Ao EDITAR, o `DialogoValores` TRAVA. Ao FECHAR, o `DialogoEtapa` só
  // AVISA — razão do dono, de antes desta peça: há contrato reajustado para baixo depois do pagamento, e
  // travar o fechamento por isso seria pior que registrar o número torto. Dizer "não pode" na tela em que
  // pode seria mentira da tela.
  if (entrada > total) return `A entrada (${brl(entrada)}) está maior que o total da venda (${brl(total)}).`;
  return null;
}

/**
 * ★★ WD-c2 — QUANDO o motivo é exigido. A regra vive AQUI, não no componente.
 *
 * Só quando o **TOTAL DA VENDA MUDA** — e só quando havia um total antes. Em detalhe:
 *
 *  - **fechando o lead** (não havia total): **não pede**. É o primeiro registro do número, não uma correção;
 *    exigir justificativa para preencher o que estava vazio seria atrito sem auditoria.
 *  - **editando e o total NÃO mudou** (acertou só entrada, código, nome ou oportunidade): **não pede**. São
 *    campos operacionais; nenhum deles entra em relatório.
 *  - **editando e o total MUDOU**: ★ **pede**. É o número que move metas, painel e placar, inclusive de meses
 *    passados — e a única fonte do que mudou é a timeline.
 */
export function exigeMotivo({ valorFechadoAnterior, valorFechadoNovo } = {}) {
  const antes = paraNumero(valorFechadoAnterior);
  const depois = paraNumero(valorFechadoNovo);
  if (!(antes > 0)) return false;        // fechando: não havia total
  return depois !== antes;               // editando: só se o número mudou
}

/** O motivo é OBRIGATÓRIO quando `exigeMotivo` diz que sim — e vive aqui, não no componente. */
export const motivoValido = (motivo) => String(motivo ?? '').trim().length > 0;

/** Só os campos que REALMENTE mudaram. Nada mudou → objeto vazio, e quem chama não grava. */
export function mudancasDe(lead, { valorFechado } = {}) {
  const mudancas = {};
  const total = Math.max(0, paraNumero(valorFechado));
  if (total !== paraNumero(lead?.valorFechado)) mudancas.valorFechado = total;
  return mudancas;
}

/**
 * A linha da timeline: DE-PARA de cada campo que mudou, mais o motivo.
 *
 * ★ Só os campos alterados entram. Uma linha que diz "entrada: R$ 900,00 → R$ 900,00" faz quem audita
 * procurar uma mudança que não houve.
 */
export function textoDaCorrecao(lead, mudancas = {}, motivo = '') {
  const de = brl(lead?.valorFechado);
  const para = brl(mudancas.valorFechado);
  return `Total da venda corrigido — ${de} → ${para}. Motivo: ${String(motivo ?? '').trim()}`;
}
